import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';
import { getCookie } from '../utils/cookies';
import { getRolePolicyBindings } from '../services/iam';

interface Role {
  id: string;
  key: string;
  name: string;
  is_admin?: boolean;
  is_primary?: boolean;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface Permission {
  id: string;
  policy_ref: string;
  effect: string;
  attributes: any;
}

export interface UserInfo {
  subject: string;
  email: string;
  name: string;
  groups: string[];
  roles: Role[];
  tenant?: string;
  mfa_verified?: boolean;
  session_id?: string;
  expires_at?: number;
  issued_at?: number;
  // Set by the backend's admin evaluation (role bindings + primary role); read
  // by fetchUserInfo and usePermissions.
  is_admin?: boolean;
  is_delegated_admin?: boolean;
  primary_role?: string;
}

interface UserContextType {
  userInfo: UserInfo | null;
  secondaryRoles: Role[];
  activeRole: Role | null;
  permissions: Permission[];
  isLoading: boolean;
  refreshUserInfo: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [secondaryRoles, setSecondaryRoles] = useState<Role[]>([]);
  const [activeRole, setActiveRoleState] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserInfo = async () => {
    try {
      const token = getCookie('auth_token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await authApi.getUserInfo();
      const userData = response.data;
      
      setUserInfo(userData);
      
      // Try to restore previously selected role from cookies or find admin role
      let roleToActivate: Role | null = null;
      
      if (userData.roles && userData.roles.length > 0) {
        // First, check if backend explicitly identified this user as admin/delegated-admin
        // The backend evaluates this via evaluateAdminStatus which checks role_bindings + primary_role
        const isBackendAdmin = userData.is_admin === true;
        const isBackendDelegatedAdmin = userData.is_delegated_admin === true;

        // Find an admin role to activate: check is_admin flag OR role name
        const adminRole = userData.roles.find((r: Role) => {
          if (r.is_admin === true) return true;
          const name = (r.name || '').toLowerCase();
          return name.includes('admin') || name.includes('delegated');
        });
        
        if (adminRole) {
          // Admin role takes highest priority (including delegated admin grants)
          roleToActivate = adminRole;
        } else if (isBackendAdmin || isBackendDelegatedAdmin) {
          // Backend says user is admin but no matching role found by name — use first role
          roleToActivate = userData.roles[0];
        } else if (userData.primary_role) {
          // Fall back to primary role
          roleToActivate = userData.roles.find((r: Role) => r.id === userData.primary_role) || null;
        }
        
        // If still no role, use the first available role
        if (!roleToActivate && userData.roles.length > 0) {
          roleToActivate = userData.roles[0];
        }
      }

      setSecondaryRoles(userData.roles ? userData.roles.filter((r: Role) => r.id !== roleToActivate?.id) : []);

      if (roleToActivate) {
        setActiveRoleState(roleToActivate);
        
        if(!roleToActivate?.is_admin){
          // Fetch permissions for the active role
          await fetchRolePermissions(roleToActivate.id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      // Don't clear userInfo on error to prevent flashing
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRolePermissions = async (roleId: string) => {
    try {
      const response = await getRolePolicyBindings(roleId);
      setPermissions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch role permissions:', error);
      setPermissions([]);
    }
  };

  const refreshUserInfo = async () => {
    setIsLoading(true);
    await fetchUserInfo();
  };

  useEffect(() => {
    fetchUserInfo();
  }, []);

  return (
    <UserContext.Provider
      value={{
        userInfo,
        secondaryRoles,
        activeRole,
        permissions,
        isLoading,
        refreshUserInfo,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
